import { useRef } from 'react';
import { MailIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Spinner,
  SearchError,
  FavouriteCourseResultCard,
} from '@/components';
import { useFavourites } from '@/hooks';
import { FavouriteHeartIcon } from './FavouriteHeartIcon';

export const FavouritesButton = () => {
  const { favourites, favouriteDetails, isFavouriteDetailsLoading, favouriteDetailsError, emailFavouriteCourses } =
    useFavourites();

  const areFavouritesLoading = !favouriteDetails && isFavouriteDetailsLoading;

  const hasError = !!favouriteDetailsError;

  const hasFavouriteData = !areFavouritesLoading && !hasError && favouriteDetails && favouriteDetails.length > 0;

  const showEmailForm =
    !emailFavouriteCourses.isPending && !emailFavouriteCourses.isSuccess && !emailFavouriteCourses.isError;

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const emailFavourites = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    emailFavouriteCourses.mutate({
      courseIds: favourites,
      emailAddress: formData.get('email') as string,
      name: formData.get('name') as string,
    });
  };
  return (
    favourites.length > 0 && (
      <>
        <Dialog>
          <DialogTrigger className="focus-visible:outline-magenta" title="View favourite courses">
            <span className="relative flex h-[31px] w-[32px] items-center justify-center rounded-full transition duration-150 hover:bg-[#f7f7f7]">
              <FavouriteHeartIcon favouriteCount={favourites.length} />
            </span>
          </DialogTrigger>
          <DialogContent
            className="border-none bg-white p-0 md:max-w-3xl [&>button]:right-5 [&>button]:top-[21px] md:[&>button]:right-8
              md:[&>button]:top-[32px]"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              closeButtonRef.current?.focus();
            }}
          >
            <DialogHeader className="max-h-[90vh] space-y-0 text-left">
              <div className="px-5 py-6 md:px-8 md:py-[35px]">
                <DialogTitle asChild>
                  <h2 className="font-proxima text-[20px] font-bold leading-[26px]">Favourites</h2>
                </DialogTitle>
              </div>
              <div className="overflow-auto bg-[#f7f7f7] p-5 md:p-8">
                <DialogDescription asChild>
                  <>
                    {areFavouritesLoading && <Spinner />}
                    {hasError && <SearchError />}
                    {hasFavouriteData && (
                      <>
                        <div aria-description="Favourites" className="grid gap-6 md:grid-cols-2">
                          {favouriteDetails.map((favourite, index) => (
                            <FavouriteCourseResultCard key={index} result={favourite} />
                          ))}
                        </div>
                        {showEmailForm && (
                          <form method="post" className="mt-8 w-full" onSubmit={emailFavourites}>
                            <div className="flex flex-col gap-6 md:flex-row">
                              <input
                                type="text"
                                name="name"
                                id="favs_name"
                                required
                                placeholder="Your name"
                                className="h-14 w-full rounded-lg border border-[#E8E8E8] px-4 placeholder:text-secondary-grey-01 focus:outline-none focus:ring-2
                                  focus:ring-ring md:flex-1"
                              />
                              <input
                                type="email"
                                name="email"
                                id="favs_email"
                                required
                                placeholder="Your email address"
                                className="h-14 w-full rounded-lg border border-[#E8E8E8] px-4 placeholder:text-secondary-grey-01 focus:outline-none focus:ring-2
                                  focus:ring-ring md:flex-1"
                              />
                            </div>
                            <div className="mt-6 flex justify-center rounded-md bg-ring text-white">
                              <button
                                type="submit"
                                className="inline-flex w-full items-center justify-center rounded-md border-2 border-ring px-6 py-3 font-semibold transition
                                  hover:bg-white hover:text-ring focus:outline-magenta disabled:opacity-60"
                              >
                                <span className="mr-3 flex h-6 w-6 items-center">
                                  <MailIcon />
                                </span>
                                Email my favourites
                              </button>
                            </div>
                          </form>
                        )}
                        {emailFavouriteCourses.isPending && (
                          <span className="mt-8 flex items-center justify-center">
                            <Spinner />
                          </span>
                        )}
                        {emailFavouriteCourses.isSuccess && (
                          <div className="mt-8 flex flex-col items-center justify-center">
                            <h3 className="">Done!</h3>
                            <p>Your favourites should now be in your inbox – if not, check your junk folder.</p>
                          </div>
                        )}
                        {emailFavouriteCourses.isError && (
                          <div className="mt-8 flex flex-col items-center justify-center">
                            <h3 className="">Error</h3>
                            <p>An error occured. Please try refreshing the page.</p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                </DialogDescription>
              </div>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </>
    )
  );
};
